/**
 * @file 拦截xmlhttprequest对象
 * @author dafo<huanghouin@baijiahulian.com>
 * @module util / interceptAjax
 */
import timeReport from '../time';
import {
    Config,
    HB_SESSION_ID_KEY,
    trackID,
    getUserID,
    DISABLE_INTERCEPT_API_DOAMIN_LIST
} from './config';
import extractPathname from './url';
import cookie from './cookie';
import Stack from './stack';


const BACKEND_API_GAPM_TRACEID = 'gapm-traceid';
// 全局取一次就好
let documentWidth = 0;
let documentHeight = 0;

const HB_SESSION_ID_VALUE = cookie.get(HB_SESSION_ID_KEY);
const requestStack = new Stack(20);
const po = new PerformanceObserver(list => {
    for (const entry of list.getEntries()) {
        // const entryJson = entry.toJSON();
        const {initiatorType} = entry || {};
        if (initiatorType === 'xmlhttprequest' || initiatorType === 'fetch') {
            requestStack.push(entry);
        }
    }
});

function clearBufferSize(event) {
    performance.clearResourceTimings();
}

function canMakeHTTPRequest() {
    return typeof window === 'object';
}

function isPositiveNumber(value) {
    return typeof value === 'number' && value > 0;
}

/**
 * 校验请求链接是否命中规则
 *
 * @param {string} 需要进行判断的url
 * @param {string} 需要校验的规则
 * @return {boolean} 是否命中规则
 */
function canUrlIntercept(url, rule) {
    if (typeof rule === 'string') {
        return url.includes(rule);
    }
    if (rule instanceof RegExp) {
        return rule.test(url);
    }
    return false;
}

/**
 * 是否可以拦截当前域名接口， 除了 DISABLE_INTERCEPT_API_DOAMIN_LIST 之外，都需要拦截一下
 *
 * @param {string} 需要进行判断的url
 * @return {boolean} 是否不能拦截某些接口
 */
function canNotIntercept(url = '') {
    // 多取几次appNid是为了兼容历史传参情况
    const appNid = Config.APPNID || Config.appNid || Config.appId;
    // 开关打开时且有appNid才允许拦截
    if (!Config.ALLOW_INTERCEPT || !appNid) {
        return true;
    }
    try {
        const {include = [], exclude = []} = Config.INTERCEPT_RULES;
        // 优先校验黑明单，第一优先级，符合黑名单规则的不上报
        if ([...DISABLE_INTERCEPT_API_DOAMIN_LIST, ...exclude].some(rule => canUrlIntercept(url, rule))) {
            return true;
        }
        // 如果未配置白名单，则上报非黑明单的所有url
        if (include.length === 0) {
            return false;
        }
        // 如果配置了白名单，则上报非黑明单且只在白名单的url
        if (include.some(rule => canUrlIntercept(url, rule))) {
            return false;
        }
        return true;
    }
    catch (e) {
        throw new Error(e.message);
    }
}

/**
 * 拦截接口请求，执行自定义的回调
 *
 * @param {Function} fn 回调函数
 */
export function interceptXHR(fn) {
    if (canMakeHTTPRequest()) {
        const {open} = XMLHttpRequest.prototype;

        XMLHttpRequest.prototype.open = function xhropen(...params) {
            const start = Date.now();
            const [, url = ''] = params;
            if (canNotIntercept(url)) {
                open.apply(this, params);
            }
            else {
                const readyStateChangeListener = () => {

                    /**
                     * 性能考虑，只有在xhr状态为 DONE 时，才进行后续的拦截上报操作。
                     *
                     * UNSENT（未发送）0
                     * OPENED（已打开）1
                     * HEADERS_RECEIVED () 2
                     * LOADING（下载中）3
                     * DONE（完成）4
                     */
                    // 为防止其他第三方脚本修改 XMLHttpRequest，比如oneapm，再从原型上取一下值
                    if ((this.readyState === XMLHttpRequest.DONE)
                        || (this.readyState === XMLHttpRequest.prototype.DONE)) {
                        const elapsed = Date.now() - start;
                        if (canNotIntercept(url)) {
                            this.removeEventListener('readystatechange', readyStateChangeListener);
                            return;
                        }

                        // 将当前xhr 实例， open 方法的所有参数，和额外的数据（比如性能耗时信息，以后可以继续补充）传给回调函数
                        if (typeof fn === 'function') {
                            try {
                                fn(this, params, {start, elapsed});
                            }
                            catch (e) {
                                // eslint-disable-next-line no-console
                                console.error('interceptXHR err', e);
                            }
                        }

                        this.removeEventListener('readystatechange', readyStateChangeListener);
                    }
                };

                this.addEventListener('readystatechange', readyStateChangeListener);
                open.apply(this, params);
            }

        };
    }
}

/**
 * 拦截fetch接口请求
 *
 * @param {Function} fn 处理fetch返回结果的回调函数
 */
export function interceptFetch(fn) {
    if (canMakeHTTPRequest()) {
        const oldFetch = fetch;
        window.fetch = function newfetch(...args) {
            const start = Date.now();
            return oldFetch.apply(null, args).then(fetchResponse => {
                // 请求结束之后计时，防止产生较大误差
                const elapsed = Date.now() - start;
                fn(fetchResponse, args, {start, elapsed});
                return fetchResponse;
            }, fetchResponse => {
                // 请求结束之后计时，防止产生较大误差
                const elapsed = Date.now() - start;
                // 接口由于网络失败，或者中断请求
                fn(fetchResponse, args, {start, elapsed});
                return fetchResponse;
            });
        };
    }
}

function getEntry(url, startTime) {
    const navigationStart = performance?.timeOrigin || 0;
    // 距离页面时间原点已过去*毫秒
    const timeElapsed = performance?.now() || 0;
    const timeDifference = Date.now() - navigationStart - timeElapsed;
    const len = requestStack.display().length;

    for (let i = len; i > 0; i--) {
        const entry = requestStack.display()[i - 1];
        const entryStartTime = navigationStart && entry.startTime && Math.round(navigationStart + entry.startTime);
        const difference = Math.abs(startTime - entryStartTime - timeDifference);
        if (entry.name === url && difference >= 0 && difference < 5) {
            return entry;
        }
    }

    const performanceResourceTimings = performance?.getEntriesByName(url) || [];
    const entry = performanceResourceTimings[performanceResourceTimings.length - 1] || {};
    const entryStartTime = navigationStart && entry?.startTime && Math.round(navigationStart + entry.startTime);
    const difference = Math.abs(startTime - entryStartTime - timeDifference);
    if (difference >= 0 && difference < 5) {
        return entry;
    }

    return null;
}
function getBaseData(entry, navigationStart, timeDifference) {
    const {duration, startTime} = entry;
    return {
        start: navigationStart && startTime && Math.round(navigationStart + startTime + timeDifference),
        duration: duration?.toFixed(2),
        rtt: navigator?.connection?.rtt || -1
    };
}

function getTimeData(entry) {
    const {
        startTime,
        domainLookupStart,
        domainLookupEnd,
        connectStart,
        connectEnd,
        fetchStart,
        responseStart,
        requestStart,
        responseEnd,
    } = entry;

    return {
        startTime: startTime?.toFixed(2),
        domainLookupStart: domainLookupStart?.toFixed(2),
        domainLookupEnd: domainLookupEnd?.toFixed(2),
        connectStart: connectStart?.toFixed(2),
        connectEnd: connectEnd?.toFixed(2),
        fetchStart: fetchStart?.toFixed(2),
        responseStart: responseStart?.toFixed(2),
        requestStart: requestStart?.toFixed(2),
        responseEnd: responseEnd?.toFixed(2)
    };
}

function getSizeData(entry) {
    const {
        encodedBodySize,
        decodedBodySize,
        transferSize
    } = entry;

    return {
        encodedBodySize,
        decodedBodySize,
        transferSize
    };
}

/**
 * 为给定的URL获取性能资源计时。 从performance.getEntriesByName中获取
 * @param {string} url 请求api（protocal+domain+path+query）
 * @param {Array} types 获取{base: false, times: false, size: false}中的性能资源计时信息类型
 * @returns {Object} - 包含性能资源计时信息的对象。
 */
function getPerformanceResourceTiming(url = '', startTime, types = {base: false, times: false, size: false}) {
    const entry = getEntry(url, startTime);
    if (!entry || !types) {
        return {base: {}, times: {}, size: {}};
    }
    const navigationStart = performance?.timeOrigin || 0;
    const timeElapsed = performance?.now() || 0;
    const timeDifference = Date.now() - navigationStart - timeElapsed;

    return {
        base: types.base ? getBaseData(entry, navigationStart, timeDifference) : {},
        times: types.times ? getTimeData(entry) : {},
        size: types.size ? getSizeData(entry) : {}
    };
}

/**
 * 拦截 XHR 接口请求后，执行自定义的回调
 *
 * @param {Object} xhrInstance XHR实例对象
 * @param {Array.<string>} apiParams 发起请求时带的参数
 * @param {number} elapsed 接口请求的耗时
 */
function handleXHR(xhrInstance, apiParams, {start, elapsed}) {
    // gapm traceid 是后端返回的用来跟踪每次接口请求链路的id，放在api接口的响应头里，往轻舟发起 api_call 类型请求的时候自动带上
    let apiCallTraceId = '';

    // 提前判断一下响应头是否存在 BACKEND_API_GAPM_TRACEID，如果不存在，还用 getResponseHeader 获取，chrome上就会在控制台报错，但是这个错误不影响代码后续执行
    if (xhrInstance.getAllResponseHeaders().indexOf(BACKEND_API_GAPM_TRACEID) > -1) {
        apiCallTraceId = xhrInstance.getResponseHeader(BACKEND_API_GAPM_TRACEID);
    }

    const {base} = getPerformanceResourceTiming(xhrInstance.responseURL, start, {base: true});

    // 要上报的数据
    const timingData = {
        ...getBasicApiTimeData(),
        // 操作名，一般取接口路径 params为发起xhr 请求时，OPEN方法的所有参数
        // 举个例子 apiParams = ['GET', '/api/1', true]， apiParams[1] 就是请求的接口，过滤掉接口的query 部分
        actionName: extractPathname(apiParams[1]),

        actionStart: base.start || start,

        // 接口耗时 自己计算的elapsed作为备份数据
        elapsed: base.duration || elapsed,

        // 接口状态
        code: xhrInstance.status,

        // 每次接口请求都会改变，
        betraceid: apiCallTraceId

    };

    // 接口总时长大于*ms
    if (isPositiveNumber(Config.SLOW_REQUEST_THRESHOLD) && base.duration > Config.SLOW_REQUEST_THRESHOLD) {
        const {times, size} = getPerformanceResourceTiming(xhrInstance.responseURL, start, {times: true, size: true});
        Object.assign(timingData, {
            // PerformanceResourceTiming
            prt: JSON.stringify({...times, ...size, rtt: base.rtt})
        });
    }

    if (xhrInstance.status >= 400) {
        const {responseText} = xhrInstance;

        let resData = {};
        try {
            resData = JSON.parse(responseText);
        }
        catch (error) { }

        Object.assign(timingData, {
            success: false,
            reponse: resData.data || responseText,
            request: JSON.stringify(apiParams),
            msg: resData.msg
        });
    }

    timeReport(timingData);
}

/**
 * 获取上报数据
 *
 * @param {Object} fetchResponse fetch响应的实例
 * @param {Object} apiParams fetch请求的参数对象
 * @param {number} start fetch请求起始时间戳
 * @param {Object} resData 解析接口响应的json数据
 * @param {Error} jsonErr 解析json出错Error对象
 */
function reportPerfData(fetchResponse, apiParams, start, elapsed = 0, resData = {}, jsonErr) {
    const apiCallTraceId = fetchResponse.headers.get(BACKEND_API_GAPM_TRACEID);
    const {url, ok, status} = fetchResponse;
    // 在最终拿到返回的json后结束计时

    const {base} = getPerformanceResourceTiming(fetchResponse.url, start, {base: true});

    const reportData = {
        ...getBasicApiTimeData(),

        // 操作名，一般取接口路径 params为发起xhr 请求时，OPEN方法的所有参数
        actionName: extractPathname(url),

        actionStart: base.start || start,

        // apicall backend tracid
        betraceid: apiCallTraceId,

        // 接口耗时
        elapsed: base.duration || elapsed
    };

    // 接口总时长大于*ms
    if (isPositiveNumber(Config.SLOW_REQUEST_THRESHOLD) && base.duration > Config.SLOW_REQUEST_THRESHOLD) {
        const {times, size} = getPerformanceResourceTiming(fetchResponse.url, start, {times: true, size: true});
        Object.assign(reportData, {
            // PerformanceResourceTiming
            prt: JSON.stringify({...times, ...size, rtt: base.rtt})
        });
    }

    // http 状态码 200-299
    if (ok) {
        Object.assign(reportData, {code: status});
    }
    else {
        Object.assign(reportData, {
            success: false,
            code: status,
            reponse: jsonErr ? '' : JSON.stringify(resData.data),
            request: apiParams.body || JSON.stringify(apiParams.data),
            msg: jsonErr ? jsonErr.message : resData.msg
        });
    }
    timeReport(reportData);
}

/**
 * 拦截fetch接口请求后， 执行自定义的回调
 * @param {Object} fetchResponse fetch接口返回值
 * @param {Object} apiParams fetch 接口的参数
 * @param {number} start 发起请求的起始时间戳
 */
function handleFetch(fetchResponse, apiParams, {start, elapsed}) {
    // 在 fetchResponse 中的body流没有被使用之前，先克隆一份，否则会报错。
    // 参考 https://developer.mozilla.org/zh-CN/docs/Web/API/Response/clone
    if (fetchResponse instanceof Response) {
        const clonedRes = fetchResponse.clone();
        if (canNotIntercept(clonedRes.url)) {
            return;
        }
        // 异步化，不阻塞后续流程, 降低此日志优先级
        setTimeout(() => {
            if (clonedRes.headers) {
                const contentType = clonedRes.headers.get('Content-Type') || clonedRes.headers.get('content-type');
                const jsonMIME = 'application/json';

                //  目前先处理返回值为 json 的数据
                if (contentType && contentType.includes(jsonMIME)) {
                    clonedRes.json().then(resData => {
                        reportPerfData(clonedRes, apiParams, start, elapsed, resData);
                    }, jsonerr => {
                        reportPerfData(clonedRes, apiParams, start, elapsed, {}, jsonerr);
                    });
                }
            }
        }, 0);
    }
}

/**
 * 获取上报的基础性能数据
 *
 * @returns {Object} 性能数据对象
 */
function getBasicApiTimeData() {
    // 尽量避免多次获取
    documentWidth = documentWidth || document.documentElement.clientWidth;
    documentHeight = documentHeight || document.documentElement.clientHeight;

    // 要上报的基础数据
    return {
        // 当前页 URL
        url: location.href,

        // 用户的唯一标识，由后端生成写到 cookie 中
        uuid: trackID,

        // 登录用户 id
        uid: getUserID(),

        // 请求类型
        reqType: 'api_call',

        // appid 项目在擎天云节点树上的标识
        appNid: Config.appNid || Config.appId || Config.APPNID,

        // 会话session
        [HB_SESSION_ID_KEY]: HB_SESSION_ID_VALUE || cookie.get(HB_SESSION_ID_KEY),

        // 屏幕分辨率
        deviceResolution: `${documentWidth}*${documentHeight}`,

        // 接口请求是否成功，默认成功
        success: true
    };
}

// 拦截所有接口请求
export const initInterceptXHR = () => {
    // 兼容服务端
    // eslint-disable-next-line no-negated-condition
    if (typeof window !== 'object') {
        return;
    }
    po?.observe({entryTypes: ['resource']});
    performance?.addEventListener(
        'resourcetimingbufferfull',
        clearBufferSize,
    );
    // 重写 xhr 的 open 方法， 拦截所有 api 请求， 统计耗时
    interceptXHR(handleXHR);

    // 重写 fetch 方法， 拦截所有 api 请求， 统计耗时
    interceptFetch(handleFetch);
    // TODO
    // if (Config.ENABLE_QINGZHOU_PERF) {
    // }
};
